// Typed client for the "Aktiviteter" list, promoted out of the KV bridge to a
// relational table (Stage 2). On the `http` backend these hit the dedicated
// /activities endpoint (real Postgres); on memory/tests they fall back to the
// key/value Storage the app used before (`activities_v1`), so behavior off the
// http backend is unchanged. Whole-collection GET/PUT — the app loads the full
// list and rewrites it on every change.
//
// NOTE: `persistActivities` is the WHOLE-COLLECTION write, and on the http
// backend it is now closed (#245, slice 4) — it throws rather than sending.
// Every writer names what it changes; the commands are at the foot of this
// module. Off the http backend (memory, tests) it still writes the blob, so
// behaviour there is unchanged.
import { Storage } from '../storage';
import { apiFetch } from './http';
import { SaveRefused } from '../saveWithRetry';
import { ascNullsLast, fetchAllPages } from './pagedList';

const KEY = 'activities_v1';

export async function fetchActivities(): Promise<any[]> {
  if (Storage.backend === 'http') {
    // IN PAGES (#1202), then the order the single read used: created first.
    return fetchAllPages<any>('/activities', 'activities', ascNullsLast('createdAt'));
  }
  const r = await Storage.get(KEY);
  return r?.value ? JSON.parse(r.value) : [];
}

/**
 * The whole-collection write, reached on the http backend where it is closed.
 *
 * Its own class because `useActivities.persistActivities` SWALLOWS write errors
 * by design — the blob path is optimistic, and that is deliberate. Swallowing
 * this one would make the closed door silent, which is the one thing it must
 * not be, so the wrapper rethrows this and only this.
 */
export class WholeCollectionWriteClosed extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'WholeCollectionWriteClosed';
  }
}

export async function persistActivities(list: any[]): Promise<void> {
  if (Storage.backend === 'http') {
    // THE DOOR IS SHUT (#245, slice 4).
    //
    // This rewrote the club's entire activity list from a copy the browser read
    // earlier, so any save could erase a member's registration or a pad's
    // completion, silently. Every writer now names what it changes, and the
    // server refuses this route with 405.
    //
    // It throws HERE rather than letting the request go and fail, because the
    // failure has to be impossible to mistake for a save: the old callers wrap
    // this in `try {} catch {}` and swallow, so a quiet 405 would look exactly
    // like success. A new caller reaching this has taken a wrong turn — the
    // commands are in this module, above.
    throw new WholeCollectionWriteClosed(
      '[activities] Whole-collection writes are closed (#245). Use a command: ' +
        'upsertActivity, createActivities, deleteActivities, completeActivityCommand, ' +
        'setActivityLiveScoreCommand, applyActivityScopeEdit, saveActivityWithOccurrences, ' +
        'cancelActivitiesCommand.',
    );
  }
  await Storage.set(KEY, JSON.stringify(list));
}

// ── MEMBER SIGN-UP IS A COMMAND (#245) ───────────────────────────────
//
// The FIRST activity writer to leave the whole-collection path. Signing up used
// to rewrite the club's entire activity list from a copy the phone read
// earlier, so a member could erase an activity staff created since. Now the
// request names one activity and the server decides the rest — who this is,
// whether there is room, the rating at sign-up, the paid status.
//
// `PUT /activities` stays for now: staff writers have not been converted yet
// (#245), so this is one writer moving, not the door closing.

/** True when member sign-up goes through the server command. */
export const activitySignupIsServerAuthoritative = () => Storage.backend === 'http';

/** Norwegian, for a member: the server's refusals, said plainly. */
const SIGNUP_MESSAGES: Record<string, string> = {
  not_found: 'Aktiviteten finnes ikke lenger.',
  already_exists: 'Aktiviteten finnes allerede.',
  payment_required: 'Betaling er ikke klar ennå. Ta kontakt med klubben.',
  signup_closed: 'Påmeldingen er stengt for denne aktiviteten.',
  level_requirement: 'Du er utenfor nivåkravet for denne aktiviteten.',
  not_individual_signup: 'Denne aktiviteten krever at du melder deg på via aktivitetssiden.',
  player_not_found: 'Vi fant ikke spillerprofilen din. Logg inn på nytt.',
};

async function refusalFrom(res: Response, fallback: string): Promise<SaveRefused> {
  const body = (await res.json().catch(() => null)) as { error?: string } | null;
  const code = body?.error ?? null;
  return new SaveRefused((code && SIGNUP_MESSAGES[code]) || fallback, res.status, code);
}

/**
 * A 5xx or a network failure is worth retrying; a 4xx is an ANSWER and is not.
 * `saveWithRetry` decides on that distinction, so the split is made here.
 */
function throwForStatus(res: Response, fallback: string): Promise<never> {
  if (res.status >= 500) {
    return Promise.reject(new Error(`[activities] ${res.status}`)); // retryable
  }
  return refusalFrom(res, fallback).then((e) => {
    throw e;
  });
}

/** Sign the CURRENT member up. The body carries nothing — the session is who. */
export async function registerForActivity(activityId: string): Promise<any> {
  const res = await apiFetch(`/member/activities/${encodeURIComponent(activityId)}/registration`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: '{}',
  });
  if (!res.ok) await throwForStatus(res, 'Kunne ikke melde deg på.');
  return res.json();
}

/** Take the current member off. Idempotent server-side. */
export async function unregisterFromActivity(activityId: string): Promise<any> {
  const res = await apiFetch(`/member/activities/${encodeURIComponent(activityId)}/registration`, {
    method: 'DELETE',
  });
  if (!res.ok) await throwForStatus(res, 'Kunne ikke melde deg av.');
  return res.json();
}

// ── DELETE + CREATE ARE COMMANDS (#245, slice 2) ─────────────────────

/** True when activity delete/create go through the server commands. */
export const activityCommandsAreServerAuthoritative = () => Storage.backend === 'http';

export interface DeleteActivitiesIntent {
  /** The activities named outright. */
  ids?: string[];
  /** Or a recurring series, whose members the SERVER derives. */
  recurring?: { recurringId: string; scope: 'this' | 'future' | 'all'; anchorId: string };
  /**
   * Also free the courts booked for them.
   *
   * NOT DEFAULTED, on purpose. Deleting a recurring occurrence releases its
   * courts; consuming a preview activity into a tournament must not, because
   * those courts now belong to the tournament. A default would silently pick
   * one of the two.
   */
  releaseBookings: boolean;
}

export async function deleteActivities(intent: DeleteActivitiesIntent): Promise<{
  deleted: number;
  bookingsReleased: number;
}> {
  const q = new URLSearchParams();
  for (const id of intent.ids ?? []) q.append('id', id);
  if (intent.recurring) {
    q.set('recurringId', intent.recurring.recurringId);
    q.set('scope', intent.recurring.scope);
    q.set('anchorId', intent.recurring.anchorId);
  }
  // ALWAYS SENT, both values. The server refuses the request without it — an
  // omitted flag used to mean «keep the courts», which is a default the intent
  // deliberately does not have.
  q.set('releaseBookings', intent.releaseBookings ? '1' : '0');

  const res = await apiFetch(`/activities?${q.toString()}`, { method: 'DELETE' });
  if (!res.ok) await throwForStatus(res, 'Kunne ikke slette aktiviteten.');
  return res.json();
}

/** Create activities. An id that already exists is REFUSED, not replaced. */
export async function createActivities(list: any[]): Promise<any> {
  const res = await apiFetch('/activities', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ activities: list }),
  });
  if (!res.ok) await throwForStatus(res, 'Kunne ikke opprette aktiviteten.');
  return res.json();
}

/**
 * Replace-or-add ONE activity.
 *
 * The server keeps the stored `registrations` on an update — staff own the
 * configuration, members own their own signups — so whatever this sends for
 * that field is ignored unless the activity is being created.
 */
export async function upsertActivity(id: string, activity: any): Promise<any> {
  const res = await apiFetch(`/activities/${encodeURIComponent(id)}`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(activity),
  });
  if (!res.ok) await throwForStatus(res, 'Kunne ikke lagre aktiviteten.');
  return res.json();
}

// ── RUNTIME + STAFF COMMANDS (#245, slice 4) ─────────────────────────
//
// The last writers off the whole-collection path. Each names ONE activity (or
// one derived scope) and sends the REFERENCE — which match finished, which
// score, which series and anchor — never a document rebuilt from a list the
// browser read earlier.
//
// All of them return the activity as the SERVER now holds it, so the caller
// adopts that answer rather than restating its own optimistic copy.

const ACTIVITY_MESSAGES: Record<string, string> = {
  not_found: 'Aktiviteten finnes ikke lenger.',
  already_exists: 'Aktiviteten finnes allerede.',
  anchor_not_found: 'Fant ikke aktiviteten serieendringen gjelder fra.',
  anchor_date_unresolved: 'Aktiviteten mangler dato, så «denne og senere» kan ikke avgjøres.',
  not_eligible: 'Aktiviteten kan ikke avlyses automatisk lenger.',
};

async function commandJson<T>(res: Response, fallback: string): Promise<T> {
  if (!res.ok) {
    if (res.status >= 500) throw new Error(`[activities] ${res.status}`); // retryable
    const body = (await res.json().catch(() => null)) as { error?: string } | null;
    const code = body?.error ?? null;
    throw new SaveRefused((code && ACTIVITY_MESSAGES[code]) || fallback, res.status, code);
  }
  return (await res.json()) as T;
}

/**
 * Mark a single-match activity finished and link the match it produced.
 *
 * The SERVER stamps `completedAt` — a client clock would let two courts
 * disagree about when the same evening ended — and this is the same operation
 * `POST /pad/result` performs, not a second implementation of it.
 */
export async function completeActivityCommand(id: string, matchId: string | null): Promise<any> {
  const res = await apiFetch(`/activities/${encodeURIComponent(id)}/completion`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ matchId }),
  });
  const out = await commandJson<{ activity: any }>(res, 'Kunne ikke fullføre aktiviteten.');
  return out.activity;
}

/**
 * The running score for an activity, written on every point.
 *
 * Narrow BECAUSE it is high-frequency: this used to rewrite the club's entire
 * activity list on each change, so a scoreboard updated courtside could erase
 * an activity staff created a moment earlier.
 */
export async function setActivityLiveScoreCommand(id: string, liveScore: any): Promise<any> {
  const res = await apiFetch(`/activities/${encodeURIComponent(id)}/live-score`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ liveScore }),
  });
  const out = await commandJson<{ activity: any }>(res, 'Kunne ikke lagre stillingen.');
  return out.activity;
}

export interface ActivityScopeEdit {
  recurringId: string;
  anchorId: string;
  scope: 'this' | 'future' | 'all';
  config?: any;
  bookingDraft?: any;
  seriesCode?: string;
}

/**
 * A configuration edit across a recurring series.
 *
 * The client names the series and the ANCHOR; the server decides which
 * occurrences that covers. Sending a list of ids would make «future» whatever
 * the browser said it was. Only staff-owned fields are applied, so the edit
 * cannot reach a member's signup or a finished occurrence's result.
 */
export async function applyActivityScopeEdit(
  edit: ActivityScopeEdit,
): Promise<{ updated: number }> {
  const res = await apiFetch('/activities', {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(edit),
  });
  return commandJson<{ updated: number }>(res, 'Kunne ikke lagre serieendringen.');
}

/**
 * Save one activity AND create these further occurrences of it, atomically.
 *
 * One business action: the wizard builds both halves, and the old list write
 * made them atomic by accident. Two commands would be two chances to
 * half-apply — a saved activity with none of its occurrences, and no error.
 */
export async function saveActivityWithOccurrences(
  activity: any,
  occurrences: any[],
): Promise<{ activity: any; created: number }> {
  const res = await apiFetch('/activities/save-with-occurrences', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ activity, occurrences }),
  });
  return commandJson<{ activity: any; created: number }>(res, 'Kunne ikke lagre aktiviteten.');
}

/** One activity a `not_eligible` refusal names, and why the server said no. */
export interface CancelRefusal {
  id: string;
  reason: string;
}

/**
 * Auto-cancel under-subscribed activities.
 *
 * The browser decides WHICH activities look under-subscribed and proposes them;
 * the server re-derives eligibility from its own data and refuses the whole
 * request if any named activity does not qualify. Cancelling refunds people, so
 * a stale tab must not be able to cancel a healthy activity by naming it.
 */
export async function cancelActivitiesCommand(
  ids: string[],
  reason = 'too_few',
): Promise<{ cancelled: number; refunded: number; refusals?: CancelRefusal[] }> {
  const res = await apiFetch('/activities/cancel', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ ids, reason }),
  });
  if (res.status === 409) {
    const body = (await res.json().catch(() => null)) as { refusals?: CancelRefusal[] } | null;
    return { cancelled: 0, refunded: 0, refusals: body?.refusals ?? [] };
  }
  return commandJson<{ cancelled: number; refunded: number }>(
    res,
    'Kunne ikke avlyse aktiviteten.',
  );
}
